import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';

import { BoardTemplate, Workspace } from '../../mainApi';
import { TemplateInstanceInput } from '../../services/templates.service';

type CreateTemplateInstanceDialogProps = {
  template: BoardTemplate | null;
  isCreating: boolean;
  workspaces: Workspace[];
  onClose: () => void;
  onSubmit: (payload: TemplateInstanceInput, workspace: Workspace | null) => void;
};

export const CreateTemplateInstanceDialog = ({
  template,
  isCreating,
  workspaces,
  onClose,
  onSubmit,
}: CreateTemplateInstanceDialogProps) => {
  const [title, setTitle] = useState('');
  const [workspaceTitle, setWorkspaceTitle] = useState('');
  const [titleError, setTitleError] = useState('');

  useEffect(() => {
    if (!template) {
      return;
    }

    setTitle('');
    setWorkspaceTitle('');
    setTitleError('');
  }, [template]);

  const handleSubmit = () => {
    const trimmedTitle = title.trim();

    setTitleError('');

    if (!trimmedTitle) {
      setTitleError('Required');
      return;
    }

    const workspace =
      workspaces.find((currentWorkspace) => currentWorkspace.title === workspaceTitle) ?? null;

    onSubmit(
      {
        name: trimmedTitle,
        workspace: workspaceTitle.trim(),
      },
      workspace
    );
  };

  const spaceLabel =
    (template?.spaceType ?? 'board') === 'wall'
      ? 'wall'
      : (template?.spaceType ?? 'board') === 'game'
      ? 'Quick Sort'
      : 'board';

  return (
    <Dialog
      open={Boolean(template)}
      onClose={() => {
        if (!isCreating) {
          onClose();
        }
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Create {spaceLabel} from template</DialogTitle>
      <DialogContent>
        <Stack mt={1} spacing={2.5}>
          <FormControl size="small" fullWidth>
            <InputLabel id="template-instance-workspace-label">Workspace (optional)</InputLabel>
            <Select
              labelId="template-instance-workspace-label"
              value={workspaceTitle}
              label="Workspace (optional)"
              onChange={(event) => setWorkspaceTitle(event.target.value)}
              disabled={isCreating}
            >
              <MenuItem value="">None</MenuItem>
              {workspaces.map((workspace) => (
                <MenuItem key={workspace.id} value={workspace.title || ''}>
                  {workspace.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            error={Boolean(titleError)}
            helperText={titleError}
            disabled={isCreating}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isCreating}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isCreating}>
          {isCreating ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
